/**
 * GLAMRDiP announcement bar
 * - Standard mode: cycles through message blocks on an interval (respecting prefers-reduced-motion)
 * - Tiered-spend mode: reads current cart subtotal, renders the highest matching tier's message.
 *   Re-renders on cart update events.
 * - Dismissible via sessionStorage (persists for tab session only).
 *
 * No runtime dependencies. < 2KB gzipped target. Vanilla DOM + fetch.
 */
(() => {
  const bars = document.querySelectorAll('[data-announcement-bar]');
  if (!bars.length) return;

  const DISMISS_KEY_PREFIX = 'gd:announcement-dismissed:';
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  bars.forEach(bar => {
    const sectionId = bar.dataset.sectionId;
    const dismissible = bar.dataset.dismissible === 'true';
    const tiered = bar.dataset.tieredEnabled === 'true';

    // Honour sessionStorage dismissal
    if (dismissible && sessionStorage.getItem(DISMISS_KEY_PREFIX + sectionId) === '1') {
      bar.setAttribute('data-hidden', 'true');
      return;
    }

    // Dismiss wiring
    const closeBtn = bar.querySelector('[data-announcement-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        bar.setAttribute('data-hidden', 'true');
        try { sessionStorage.setItem(DISMISS_KEY_PREFIX + sectionId, '1'); } catch {}
      });
    }

    if (tiered) {
      initTiered(bar);
    } else {
      initRotating(bar);
    }
  });

  function initRotating(bar) {
    const msgs = [...bar.querySelectorAll('.gd-announcement-bar__message')];
    const interval = parseInt(bar.dataset.autoRotateMs, 10);
    if (msgs.length < 2 || !interval || prefersReducedMotion) return;

    let i = 0;
    setInterval(() => {
      msgs[i].setAttribute('data-active', 'false');
      i = (i + 1) % msgs.length;
      msgs[i].setAttribute('data-active', 'true');
    }, interval);
  }

  function initTiered(bar) {
    const host = bar.querySelector('[data-mode="tiered"]');
    if (!host) return;
    const defaultEl = host.querySelector('[data-tier-default]');
    const templates = [...host.querySelectorAll('.gd-announcement-bar__tier-template')]
      .map(t => ({
        threshold: parseInt(t.dataset.tierThreshold, 10),  // cents
        rewardType: t.dataset.tierRewardType,
        content: t.content.cloneNode(true),
      }))
      .sort((a, b) => a.threshold - b.threshold);

    const render = (totalCents) => {
      // Find the highest tier whose threshold is <= cart subtotal
      let active = null;
      for (const t of templates) {
        if (totalCents >= t.threshold) active = t;
      }
      // Clear previous active
      host.querySelectorAll('.gd-announcement-bar__text:not([data-tier-default])').forEach(n => n.remove());
      if (active) {
        defaultEl && (defaultEl.style.display = 'none');
        host.appendChild(active.content.cloneNode(true));
      } else {
        defaultEl && (defaultEl.style.display = '');
      }
    };

    const fetchCart = () => fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(r => r.ok ? r.json() : null)
      .catch(() => null);

    fetchCart().then(cart => { if (cart) render(cart.total_price); });

    // Re-evaluate on common cart-change signals
    ['cart:updated', 'product-added-to-cart', 'cart-should-be-updated'].forEach(evt => {
      document.body.addEventListener(evt, () => { fetchCart().then(cart => cart && render(cart.total_price)); });
    });
  }
})();
